import { useCallback, useEffect, useState } from "react";
import { Plus, BookOpen, Pencil, Trash2, Eye, X } from "lucide-react";
import { toast } from "sonner";
import { API, fmtDate } from "@/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

const emptyDept = { name: "", description: "", color: "#4F46E5" };
const emptySop = { title: "", purpose: "", steps: [""] };

export default function Sops() {
  const [departments, setDepartments] = useState([]);
  const [activeDept, setActiveDept] = useState(null);
  const [sops, setSops] = useState([]);
  const [deptOpen, setDeptOpen] = useState(false);
  const [deptForm, setDeptForm] = useState(emptyDept);
  const [sopOpen, setSopOpen] = useState(false);
  const [sopForm, setSopForm] = useState(emptySop);
  const [editSopId, setEditSopId] = useState(null);
  const [viewSop, setViewSop] = useState(null);

  const loadDepts = useCallback(async () => {
    const { data } = await API.get("/departments");
    setDepartments(data);
    setActiveDept((cur) => cur || data[0]?.id || null);
  }, []);

  useEffect(() => {
    loadDepts().catch(() => {});
  }, [loadDepts]);

  useEffect(() => {
    if (activeDept) API.get("/sops", { params: { department_id: activeDept } }).then((r) => setSops(r.data)).catch(() => {});
    else setSops([]);
  }, [activeDept]);

  const saveDept = async () => {
    if (!deptForm.name.trim()) return toast.error("Department name is required");
    const { data } = await API.post("/departments", deptForm);
    setDeptOpen(false);
    setDeptForm(emptyDept);
    toast.success("Department created");
    await loadDepts();
    setActiveDept(data.id);
  };

  const removeDept = async (id) => {
    await API.delete(`/departments/${id}`);
    toast.success("Department deleted");
    setActiveDept(null);
    loadDepts();
  };

  const reloadSops = async () => {
    if (activeDept) {
      const { data } = await API.get("/sops", { params: { department_id: activeDept } });
      setSops(data);
    }
  };

  const saveSop = async () => {
    if (!sopForm.title.trim()) return toast.error("SOP title is required");
    const payload = { ...sopForm, department_id: activeDept, steps: sopForm.steps.filter((s) => s.trim()) };
    if (editSopId) {
      await API.patch(`/sops/${editSopId}`, payload);
      toast.success("SOP updated");
    } else {
      await API.post("/sops", payload);
      toast.success("SOP created");
    }
    setSopOpen(false);
    setSopForm(emptySop);
    setEditSopId(null);
    reloadSops();
  };

  const removeSop = async (id) => {
    await API.delete(`/sops/${id}`);
    toast.success("SOP deleted");
    reloadSops();
  };

  const setStep = (i, v) => setSopForm((p) => ({ ...p, steps: p.steps.map((s, idx) => (idx === i ? v : s)) }));

  return (
    <div className="space-y-6" data-testid="sops-page">
      <div>
        <h1 className="font-heading text-3xl font-bold text-slate-900">Standard Operating Procedures</h1>
        <p className="text-sm text-slate-500">Create departments and document how work gets done</p>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-4">
        <div className="space-y-2">
          <div className="mb-3 flex items-center justify-between">
            <span className="text-xs font-semibold uppercase tracking-widest text-slate-500">Departments</span>
            <Button variant="outline" size="icon" className="h-7 w-7" onClick={() => setDeptOpen(true)} data-testid="add-department-button" aria-label="Add department">
              <Plus className="h-4 w-4" />
            </Button>
          </div>
          {departments.map((d) => (
            <div
              key={d.id}
              onClick={() => setActiveDept(d.id)}
              className={`group flex cursor-pointer items-center justify-between rounded-md border p-3 transition-colors duration-150 ${activeDept === d.id ? "border-indigo-300 bg-indigo-50" : "border-slate-200 bg-white hover:bg-slate-50"}`}
              data-testid={`department-${d.id}`}
            >
              <div className="flex items-center gap-2">
                <span className="h-2.5 w-2.5 rounded-full" style={{ background: d.color }} />
                <div>
                  <div className="text-sm font-semibold text-slate-900">{d.name}</div>
                  {d.description && <div className="text-xs text-slate-500">{d.description}</div>}
                </div>
              </div>
              <Button variant="ghost" size="icon" className="h-7 w-7 opacity-0 transition-opacity duration-150 group-hover:opacity-100" onClick={(e) => { e.stopPropagation(); removeDept(d.id); }} data-testid={`delete-department-${d.id}`} aria-label="Delete department">
                <Trash2 className="h-3.5 w-3.5 text-rose-500" />
              </Button>
            </div>
          ))}
          {departments.length === 0 && (
            <div className="rounded-md border border-dashed border-slate-300 p-4 text-center text-xs text-slate-400" data-testid="no-departments">
              Create your first department — Sales, Purchase, Warehouse, Accounts...
            </div>
          )}
        </div>

        <div className="lg:col-span-3">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="font-heading text-xl font-semibold text-slate-900">
              {departments.find((d) => d.id === activeDept)?.name || "Select a department"}
            </h2>
            {activeDept && (
              <Button className="bg-indigo-600 hover:bg-indigo-700" onClick={() => { setSopForm(emptySop); setEditSopId(null); setSopOpen(true); }} data-testid="new-sop-button">
                <Plus className="mr-1 h-4 w-4" /> New SOP
              </Button>
            )}
          </div>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2" data-testid="sops-grid">
            {sops.map((s) => (
              <Card key={s.id} className="border-slate-200 transition-shadow duration-200 hover:shadow-md" data-testid={`sop-card-${s.id}`}>
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between">
                    <CardTitle className="flex items-center gap-2 text-base">
                      <BookOpen className="h-4 w-4 text-indigo-500" /> {s.title}
                    </CardTitle>
                    <Badge variant="secondary">v{s.version}</Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  {s.purpose && <p className="mb-3 text-sm text-slate-600">{s.purpose}</p>}
                  <div className="text-xs text-slate-400">{s.steps.length} steps · by {s.created_by} · updated {fmtDate(s.updated_at)}</div>
                  <div className="mt-3 flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => setViewSop(s)} data-testid={`view-sop-${s.id}`}>
                      <Eye className="mr-1 h-3.5 w-3.5" /> View
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => { setSopForm({ title: s.title, purpose: s.purpose, steps: s.steps.length ? s.steps : [""] }); setEditSopId(s.id); setSopOpen(true); }} data-testid={`edit-sop-${s.id}`}>
                      <Pencil className="mr-1 h-3.5 w-3.5" /> Edit
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => removeSop(s.id)} data-testid={`delete-sop-${s.id}`}>
                      <Trash2 className="h-3.5 w-3.5 text-rose-500" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
            {activeDept && sops.length === 0 && (
              <div className="col-span-2 rounded-lg border border-dashed border-slate-300 p-10 text-center text-sm text-slate-400" data-testid="no-sops">
                No SOPs in this department yet.
              </div>
            )}
          </div>
        </div>
      </div>

      <Dialog open={deptOpen} onOpenChange={setDeptOpen}>
        <DialogContent data-testid="department-dialog">
          <DialogHeader><DialogTitle>New Department</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Name *</Label>
              <Input value={deptForm.name} onChange={(e) => setDeptForm({ ...deptForm, name: e.target.value })} placeholder="e.g. Sales, Purchase, Warehouse" className="mt-1.5" data-testid="department-name-input" />
            </div>
            <div>
              <Label>Description</Label>
              <Input value={deptForm.description} onChange={(e) => setDeptForm({ ...deptForm, description: e.target.value })} className="mt-1.5" data-testid="department-description-input" />
            </div>
            <div>
              <Label>Color</Label>
              <Input type="color" value={deptForm.color} onChange={(e) => setDeptForm({ ...deptForm, color: e.target.value })} className="mt-1.5 h-10 w-20 p-1" data-testid="department-color-input" />
            </div>
            <Button className="bg-indigo-600 hover:bg-indigo-700" onClick={saveDept} data-testid="save-department-button">Create Department</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={sopOpen} onOpenChange={setSopOpen}>
        <DialogContent className="max-w-2xl" data-testid="sop-dialog">
          <DialogHeader><DialogTitle>{editSopId ? "Edit SOP" : "New SOP"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Title *</Label>
              <Input value={sopForm.title} onChange={(e) => setSopForm({ ...sopForm, title: e.target.value })} placeholder="e.g. IndiaMart Lead Handling Process" className="mt-1.5" data-testid="sop-title-input" />
            </div>
            <div>
              <Label>Purpose</Label>
              <Textarea value={sopForm.purpose} onChange={(e) => setSopForm({ ...sopForm, purpose: e.target.value })} placeholder="Why this SOP exists" className="mt-1.5" data-testid="sop-purpose-input" />
            </div>
            <div>
              <Label>Steps</Label>
              <div className="mt-1.5 space-y-2">
                {sopForm.steps.map((s, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <span className="w-6 text-sm font-semibold text-slate-400">{i + 1}.</span>
                    <Input value={s} onChange={(e) => setStep(i, e.target.value)} data-testid={`sop-step-input-${i}`} />
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setSopForm((p) => ({ ...p, steps: p.steps.filter((_, idx) => idx !== i) }))} disabled={sopForm.steps.length === 1} data-testid={`sop-step-remove-${i}`} aria-label="Remove step">
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
              <Button variant="outline" size="sm" className="mt-2" onClick={() => setSopForm((p) => ({ ...p, steps: [...p.steps, ""] }))} data-testid="sop-add-step-button">
                <Plus className="mr-1 h-3.5 w-3.5" /> Add Step
              </Button>
            </div>
            <Button className="bg-indigo-600 hover:bg-indigo-700" onClick={saveSop} data-testid="save-sop-button">{editSopId ? "Save Changes" : "Create SOP"}</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!viewSop} onOpenChange={(o) => !o && setViewSop(null)}>
        <DialogContent className="max-w-2xl" data-testid="sop-view-dialog">
          {viewSop && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading text-xl">{viewSop.title} <Badge variant="secondary" className="ml-2">v{viewSop.version}</Badge></DialogTitle>
              </DialogHeader>
              {viewSop.purpose && <p className="text-sm text-slate-600">{viewSop.purpose}</p>}
              <ol className="mt-4 space-y-3" data-testid="sop-steps-view">
                {viewSop.steps.map((s, i) => (
                  <li key={i} className="flex gap-3">
                    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-indigo-100 text-xs font-bold text-indigo-700">{i + 1}</span>
                    <span className="text-sm text-slate-800">{s}</span>
                  </li>
                ))}
              </ol>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
